import {
  QUESTIONS,
  type DuelCategory,
  type DuelQuestion,
} from "./questions";

/**
 * Pure scoring + question-selection logic for Chem Duel.
 *
 * The page owns turn/HP/timer state via React (and useHotSeatMatch). This
 * module is just the deterministic rules: how much damage a move does,
 * which questions to draw, and when to switch to harder difficulty.
 */

export const STARTING_HP = 100;
export const TURN_SECONDS = 10;
export const FAST_THRESHOLD_S = 4; // ≤ this counts as a "fast" answer
export const SUDDEN_DEATH_TURN = 10; // after this turn, escalate difficulty
export const SUDDEN_DEATH_HP_FLOOR = 80; // both must still be above this to trigger

export type DamageOutcome =
  | "correct-fast"
  | "correct-slow"
  | "wrong"
  | "timeout";

export const DAMAGE: Record<DamageOutcome, number> = {
  "correct-fast": 20, // dealt to opponent
  "correct-slow": 12, // dealt to opponent
  wrong: 8, // self-damage
  timeout: 5, // self-damage
};

export type ResolvedMove = {
  outcome: DamageOutcome;
  /** Damage dealt to opponent (0 if self-damage). */
  opponentDamage: number;
  /** Damage dealt to current player (0 if correct). */
  selfDamage: number;
};

/**
 * Resolve a single answer attempt into damage numbers. Pure.
 *
 * @param chosenIndex  Player's selected option, or null if they timed out
 * @param question     The question being answered
 * @param elapsedMs    Wall-clock time taken in ms (ignored on timeout)
 */
export function resolveAnswer(
  chosenIndex: number | null,
  question: DuelQuestion,
  elapsedMs: number,
): ResolvedMove {
  if (chosenIndex === null) {
    return {
      outcome: "timeout",
      opponentDamage: 0,
      selfDamage: DAMAGE.timeout,
    };
  }
  if (chosenIndex === question.correctIndex) {
    const fast = elapsedMs <= FAST_THRESHOLD_S * 1000;
    const outcome: DamageOutcome = fast ? "correct-fast" : "correct-slow";
    return {
      outcome,
      opponentDamage: DAMAGE[outcome],
      selfDamage: 0,
    };
  }
  return {
    outcome: "wrong",
    opponentDamage: 0,
    selfDamage: DAMAGE.wrong,
  };
}

/** Whether sudden-death difficulty escalation should kick in. */
export function isSuddenDeath(turnNumber: number, hp1: number, hp2: number): boolean {
  return (
    turnNumber > SUDDEN_DEATH_TURN &&
    hp1 > SUDDEN_DEATH_HP_FLOOR &&
    hp2 > SUDDEN_DEATH_HP_FLOOR
  );
}

export type DrawOptions = {
  /** IDs already shown this match - won't be picked again until pool exhausted. */
  exclude: ReadonlySet<string>;
  /** True after sudden death triggers - pull only difficulty 3. */
  hardOnly: boolean;
  /** Optional category filter; default: all. */
  categories?: ReadonlySet<DuelCategory>;
};

/**
 * Draw the next question. Tries to honor exclusions; if every candidate
 * would be excluded, falls back to the unfiltered pool (so a long match
 * never deadlocks).
 */
export function drawQuestion(
  rng: () => number,
  opts: DrawOptions,
): DuelQuestion {
  const matchesFilters = (q: DuelQuestion) => {
    if (opts.categories && !opts.categories.has(q.category)) return false;
    if (opts.hardOnly && q.difficulty !== 3) return false;
    return true;
  };

  const filtered = QUESTIONS.filter(matchesFilters);
  const fresh = filtered.filter((q) => !opts.exclude.has(q.id));
  const pool = fresh.length > 0 ? fresh : filtered;

  if (pool.length === 0) {
    // Should never happen - fall back to entire bank.
    return QUESTIONS[Math.floor(rng() * QUESTIONS.length)];
  }
  return pool[Math.floor(rng() * pool.length)];
}